
import React, { useState } from 'react';
import { siteConfig } from '../config/site';
import { Zap, ChevronRight, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../App';

// Define GalleryItem as React.FC to include standard props like 'key' in the type definition
const GalleryItem: React.FC<{ id: string, type: 'reel' | 'archive' }> = ({ id, type }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const isVideo = type === 'reel';
  
  const thumbnailUrl = isVideo 
    ? `https://vumbnail.com/${id}.jpg`
    : `https://drive.google.com/thumbnail?id=${id}&sz=w1000`;

  const videoUrl = isVideo
    ? `https://player.vimeo.com/video/${id}?autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479`
    : `https://drive.google.com/file/d/${id}/preview`;

  return (
    <div className="reveal reveal-up active relative group bg-black border border-white/5 hover:border-accent/40 transition-all duration-700 shadow-xl overflow-hidden">
      <div className={`relative w-full overflow-hidden bg-black ${type === 'reel' ? 'aspect-[9/16]' : 'aspect-square sm:aspect-[4/3]'}`}>
        {!isPlaying ? (
          <div className="absolute inset-0 cursor-pointer" onClick={() => setIsPlaying(true)}>
            {/* Clean Image - No Heavy Overlays */}
            <img 
              src={thumbnailUrl} 
              alt="Gallery Item" 
              className="w-full h-full object-cover transition-all duration-1000 scale-100 group-hover:scale-105" 
              loading="lazy" 
            />
            
            {/* Subtle Gradient only on hover for depth */}
            <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
        ) : (
          <iframe 
            src={videoUrl} 
            className="absolute inset-0 w-full h-full" 
            frameBorder="0" 
            allow="autoplay; fullscreen; picture-in-picture" 
            title="Santi Bladez Video"
          ></iframe>
        )}
      </div>

      {/* Aesthetic Corners */}
      <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-accent/0 group-hover:border-accent/40 transition-all"></div>
      <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-accent/0 group-hover:border-accent/40 transition-all"></div>
    </div>
  );
};

const GalleryPage: React.FC = () => {
  const { t, openBooking } = useLanguage();
  return (
    <div className="bg-primary pt-24 sm:pt-32 pb-16 overflow-hidden">
      {/* Tighter Header */}
      <section className="px-6 mb-16 relative">
        <div className="max-w-7xl mx-auto text-center reveal reveal-up active">
          <span className="block text-accent font-black tracking-[0.6em] text-[9px] uppercase mb-4 opacity-40">
            {t.visualPortfolio}
          </span>
          <h1 className="text-4xl sm:text-6xl font-serif font-bold uppercase tracking-tighter mb-6 leading-tight">
            {t.grandArchive}
          </h1>
          <div className="w-16 h-px bg-accent/20 mx-auto mb-8"></div>
          <p className="max-w-xl mx-auto text-white/30 text-base italic font-light leading-relaxed">
            {t.galleryDesc}
          </p>
        </div>
      </section>

      {/* Tighter Video Grid */}
      <section className="px-6 mb-20 relative">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {/* Reels Section */}
            {siteConfig.videos.masteryReels?.map((video) => (
              <GalleryItem key={video.id} id={video.id} type="reel" />
            ))}
            {/* Archive Section */}
            {siteConfig.videos.driveArchive?.map((item) => (
              <GalleryItem key={item.id} id={item.id} type="archive" />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - More compact */}
      <section className="py-20 px-6 text-center border-t border-white/5 bg-secondary/10">
        <div className="max-w-2xl mx-auto reveal reveal-up active">
           <h2 className="text-3xl sm:text-4xl font-serif font-bold uppercase tracking-widest mb-10 leading-tight">
             {t.readyMasterpiece}
           </h2>
           <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={openBooking}
                className="w-full sm:w-auto px-10 py-5 bg-accent text-black font-black uppercase tracking-[0.3em] text-[10px] hover:bg-white transition-all duration-700 shadow-xl"
              >
                {t.bookNow}
              </button>
              <Link 
                to="/" 
                className="w-full sm:w-auto px-10 py-5 border border-white/10 text-white/40 font-black uppercase tracking-[0.3em] text-[10px] hover:border-accent hover:text-accent transition-all duration-700 flex items-center justify-center gap-2"
              >
                {t.backToHome} <ChevronRight size={12} />
              </Link>
           </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
